import useVersionManagement from './useVersionManagement';
import { extractIntegrations } from '../utils/integrationParser';

/**
 * Custom hook to manage integration versions
 * Stores and tracks different versions of integrations from regenerations
 */
const useIntegrationVersions = (conversation, isStreaming, streamingContent) => {
  // Configuration for integration version management
  const integrationConfig = {
    // Determine if a message contains integrations
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      const lowerContent = content.toLowerCase();

      // Check for required keywords
      const hasKeywords = (
        lowerContent.includes('oauth') ||
        lowerContent.includes('api') ||
        lowerContent.includes('bearer') ||
        lowerContent.includes('integration') ||
        lowerContent.includes('authentication') ||
        lowerContent.includes('endpoint')
      );

      // Method 1: Explicit "Integration N:" pattern (highest priority, most reliable)
      const hasIntegrationPattern = /(?:^|\n)(?:Integration|Int\.?)\s+\d+\s*:/i.test(content);

      // Method 2: Has ## headings + keywords (backward compatibility)
      const hasOldFormat = content.includes('## ') && hasKeywords;

      // Accept if either method matches
      return hasIntegrationPattern || hasOldFormat;
    },

    // Parse integrations from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return extractIntegrations(content);
    },

    // Extract version information from a message
    extractVersions: (msg, parsedIntegrations, versionNumber, isPreview = false) => {
      return {
        id: msg._id || msg.id || `${msg.createdAt}-${versionNumber}`,
        timestamp: msg.createdAt || new Date().toISOString(),
        integrations: parsedIntegrations,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent
  };

  // Use the generic version management hook
  const versionData = useVersionManagement(integrationConfig);

  // Map the generic version data to integration-specific structure
  return {
    versions: versionData.versions,
    currentVersion: versionData.currentVersion ? {
      ...versionData.currentVersion,
      integrations: versionData.currentVersion.parsedContent || versionData.currentVersion.integrations
    } : null,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useIntegrationVersions;
